import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Typography,
  LinearProgress,
  Alert
} from '@mui/material';
import api from '../services/api';
import { resolveSpendSelection } from '../utils/categoryAssign';

// Configuration constants
const IMPACT_CALCULATION_DEBOUNCE_MS = 500; // Delay before recalculating budget impact

// Format currency helper
const formatCurrency = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const AddExpenseModal = ({ open, onClose, onSuccess, initialCategoryId, initialBudgetId }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isReimbursable, setIsReimbursable] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [budgetImpact, setBudgetImpact] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeBudget, setActiveBudget] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCategoryId(initialCategoryId || '');
      fetchBudgetData();
    }
    // Prefill runs when the sheet opens, including a category Spend handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCategoryId, initialBudgetId]);

  const fetchBudgetData = async () => {
    try {
      const response = await api.get('/budgets');
      const budgets = response.data || [];
      const selection = resolveSpendSelection(budgets, initialBudgetId, initialCategoryId);
      if (selection.budget) {
        setActiveBudget(selection.budget);
        setCategories(selection.budget.categories || []);
        setCategoryId(selection.categoryId);
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
    }
  };

  // Calculate budget impact when amount changes
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && activeBudget) {
      const calculateImpact = async () => {
        try {
          const amountCents = Math.round(parseFloat(amount) * 100);
          const response = await api.post('/budgets/calculate-impact', {
            amountCents,
            categoryId: categoryId || undefined,
            budgetId: activeBudget._id
          });
          
          if (response.data.impacts && response.data.impacts.length > 0) {
            setBudgetImpact(response.data.impacts[0]);
          }
        } catch (error) {
          console.error('Error calculating impact:', error);
          setBudgetImpact(null);
        }
      };
      
      // Debounce calculation
      const timeout = setTimeout(calculateImpact, IMPACT_CALCULATION_DEBOUNCE_MS);
      return () => clearTimeout(timeout);
    } else {
      setBudgetImpact(null);
    }
  }, [amount, categoryId, activeBudget]);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const amountCents = Math.round(parseFloat(amount) * 100);
      const payload = {
        amountCents,
        date: new Date(date).toISOString(),
        description,
        merchantName,
        categoryId: categoryId || undefined,
        budgetId: activeBudget?._id,
        isReimbursable,
        reimbursableCents: isReimbursable ? amountCents : 0,
        idempotencyKey: `manual:web:${Date.now()}`
      };
      
      // Handle receipt upload if present
      if (receiptFile) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          payload.receiptBase64 = reader.result.split(',')[1]; // Remove data:image prefix
          await submitExpense(payload);
        };
        reader.readAsDataURL(receiptFile);
      } else {
        await submitExpense(payload);
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      setError(error.response?.data?.error || 'Failed to add expense');
      setSubmitting(false);
    }
  };

  const submitExpense = async (payload) => {
    try {
      const response = await api.post('/transactions/manual', payload);
      
      // Show success message
      let message = 'Expense added successfully!';
      if (response.data.reconciliation === 'linked') {
        message = 'Linked to existing bank transaction!';
      } else {
        message = response.data.message || message;
      }
      
      if (onSuccess) {
        onSuccess(message);
      }
      
      handleClose();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add expense');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setAmount('');
    setDate(new Date().toISOString().split('T')[0]);
    setDescription('');
    setMerchantName('');
    setCategoryId('');
    setIsReimbursable(false);
    setReceiptFile(null);
    setBudgetImpact(null);
    setError('');
    setSubmitting(false);
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Receipt image must be less than 5MB');
        return;
      }
      setReceiptFile(file);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Expense</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Amount */}
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            required
            inputProps={{ step: '0.01', min: '0' }}
            helperText="Enter amount in dollars (e.g., 12.50)"
          />
          
          {/* Date */}
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          
          {/* Merchant */}
          <TextField
            label="Merchant"
            value={merchantName}
            onChange={(e) => setMerchantName(e.target.value)}
            fullWidth
            placeholder="Where did you spend?"
          />
          
          {/* Description */}
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="What was it for?"
          />
          
          {/* Category */}
          {categories.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Category (optional)</InputLabel>
              <Select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                label="Category (optional)"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* Reimbursable */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isReimbursable}
                onChange={(e) => setIsReimbursable(e.target.checked)}
              />
            }
            label="Mark as reimbursable"
          />
          
          {/* Receipt Upload */}
          <Box>
            <Button
              variant="outlined"
              component="label"
              fullWidth
            >
              {receiptFile ? `Receipt: ${receiptFile.name}` : 'Upload Receipt (optional)'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>
          
          {/* Budget Impact Preview */}
          {budgetImpact && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Budget Impact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Category:</Typography>
                  <Typography variant="body2">{budgetImpact.categoryName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Current:</Typography>
                  <Typography variant="body2">{formatCurrency(budgetImpact.currentSpent)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">After:</Typography>
                  <Typography variant="body2">{formatCurrency(budgetImpact.afterSpent)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Remaining:</Typography>
                  <Typography 
                    variant="body2"
                    sx={{ color: budgetImpact.remaining < 0 ? 'error.main' : 'success.main' }}
                  >
                    {formatCurrency(budgetImpact.remaining)}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(budgetImpact.percentUsed, 100)}
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: budgetImpact.percentUsed > 100 
                        ? 'error.main' 
                        : budgetImpact.percentUsed > 80 
                          ? 'warning.main' 
                          : 'success.main'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" align="center">
                  {budgetImpact.percentUsed.toFixed(0)}% used
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Reassurance Copy */}
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Don't worry—if a matching bank transaction comes in, we'll auto-link it so you won't get duplicates.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={submitting || !amount || parseFloat(amount) <= 0}
        >
          {submitting ? 'Adding...' : 'Add Expense'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExpenseModal;

